import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useVoiceCommands — wake-word voice control for the AR mirror.
 *
 * Built on the Web Speech API (`SpeechRecognition` / `webkitSpeechRecognition`).
 * Supported in Chrome / Edge / Safari (iOS 14.5+) and Capacitor's WKWebView.
 * Falls back to `supported: false` on Firefox and any browser without
 * the API — UI just hides the toggle.
 *
 * Usage:
 *   const voice = useVoiceCommands({ enabled: true, onCommand });
 *   onCommand("next" | "previous" | "done" | "snap" | "exit")
 *
 * Wake word: "Hey Trouvé" (also accepts "Hi Trouvé" / "Trouvé" alone
 * since the accent is hard for English speech recognizers — we look
 * for the consonant skeleton "tr-v" so "trove", "true-vay", "trouvé"
 * all match).
 *
 * Commands recognised within ~4s of the wake word:
 *   • "next" / "next step" / "continue" / "go" / "advance" / "okay" → "next"
 *   • "back" / "previous" / "go back"                                → "previous"
 *   • "done" / "mark done" / "finished"                              → "done"
 *   • "snap" / "snapshot" / "capture" / "photo" / "picture"          → "snap"
 *   • "stop" / "exit" / "close" / "quit" / "bye"                     → "exit"
 *
 * After dispatch, listening stays active in continuous mode so the
 * user can chain commands.
 */

// Acceptable wake-word forms — the ASR mangles "Trouvé" badly,
// especially across accents. We match a permissive set of tokens
// that all share the t-r-v consonant skeleton.
//
// Why so many variants?
// "Trouvé" is French, and English-trained ASR engines (Web Speech API,
// Chrome / Safari / Edge) very rarely transcribe it correctly. The
// pronunciation falls roughly between "TROO-vay" and "TRUH-vay", and
// the recognizer almost always picks one of these spellings:
//   trove, trovay, true vay, tru vay, truvy, truvee, tru vee, roo vay,
//   rue vay, ru vay, troof, troofy, trooved, trouvet, trovet, tru-vey
// We accept all of them — false positives are extremely unlikely
// since none of these are common English words.
const WAKE_TOKENS = [
  "trouvé",
  "trouve",
  "trouvet",
  "trovet",
  "trove",
  "trovay",
  "trovey",
  "trouvey",
  "true vay",
  "tru vay",
  "tru-vay",
  "tru-veh",
  "tru veh",
  "tru-vey",
  "tru vey",
  "truvy",
  "truvee",
  "tru vee",
  "true vee",
  "roo vay",
  "rue vay",
  "ru vay",
  "ru-vay",
  "troof",
  "troofy",
  "trooved",
  "truved",
  "true-vay",
];

const COMMAND_PATTERNS = [
  { intent: "next", words: ["next", "continue", "advance", "go", "okay", "ok", "ready"] },
  { intent: "previous", words: ["back", "previous", "go back", "undo"] },
  { intent: "done", words: ["done", "finished", "mark done", "complete", "completed"] },
  { intent: "snap", words: ["snap", "snapshot", "capture", "photo", "picture", "selfie"] },
  { intent: "exit", words: ["stop", "exit", "close", "quit", "bye", "leave"] },
];

const _normalise = (s) => s.trim().toLowerCase().replace(/[.,!?]/g, "");

const _matchWake = (text) => {
  const t = _normalise(text);
  return WAKE_TOKENS.some((w) => t.includes(w));
};

const _matchCommand = (text) => {
  const t = _normalise(text);
  // Strip everything up to and including the wake word so "hey trouvé
  // next step" matches "next" not "hey".
  const stripped = (() => {
    for (const w of WAKE_TOKENS) {
      const i = t.indexOf(w);
      if (i >= 0) return t.slice(i + w.length).trim();
    }
    return t;
  })();
  // Build a flat list of {word, intent} sorted by word length desc so
  // multi-word phrases ("go back", "mark done") win against shorter
  // overlapping single words ("go", "done") in a different intent.
  const candidates = [];
  for (const p of COMMAND_PATTERNS) {
    for (const w of p.words) candidates.push({ word: w, intent: p.intent });
  }
  candidates.sort((a, b) => b.word.length - a.word.length);
  for (const { word, intent } of candidates) {
    if (
      stripped === word ||
      stripped.startsWith(word + " ") ||
      stripped.endsWith(" " + word) ||
      stripped.includes(" " + word + " ")
    ) {
      return intent;
    }
  }
  return null;
};

export const isVoiceCommandsSupported = () => {
  if (typeof window === "undefined") return false;
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

export const useVoiceCommands = ({ enabled, onCommand, ttsActive }) => {
  const [supported, setSupported] = useState(isVoiceCommandsSupported());
  const [listening, setListening] = useState(false);
  const [lastIntent, setLastIntent] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const recognitionRef = useRef(null);
  const wakeArmedRef = useRef(false);
  const wakeArmTimerRef = useRef(null);
  const restartGuardRef = useRef(false);

  // Keep onCommand fresh without re-creating the recognizer
  const onCommandRef = useRef(onCommand);
  useEffect(() => {
    onCommandRef.current = onCommand;
  }, [onCommand]);

  // Pause MATCHING (not listening) while TTS is speaking so Trouvé
  // doesn't accidentally hear herself say "next" mid-instruction.
  // We keep the recognizer running and BUFFER the most recent transcript,
  // then re-evaluate it when TTS ends — that way the user can say
  // "hey trouvé next" while Trouvé is still talking and the command
  // fires the instant her voice stops, instead of the user having to
  // wait for silence and repeat themselves. This is the #1 source of
  // the "I said it and nothing happened" complaint.
  const ttsActiveRef = useRef(false);
  const bufferedTranscriptRef = useRef("");
  const bufferedAtRef = useRef(0);
  useEffect(() => {
    const wasActive = ttsActiveRef.current;
    ttsActiveRef.current = !!ttsActive;
    // Edge: TTS just ended → re-evaluate the buffered transcript so a
    // command spoken DURING the TTS still fires.
    if (wasActive && !ttsActive && bufferedTranscriptRef.current) {
      const fresh = Date.now() - bufferedAtRef.current < 5000;
      const text = bufferedTranscriptRef.current;
      bufferedTranscriptRef.current = "";
      bufferedAtRef.current = 0;
      if (fresh) {
        if (_matchWake(text)) {
          const cmd = _matchCommand(text);
          if (cmd) {
            setLastIntent({ intent: cmd, at: Date.now() });
            onCommandRef.current?.(cmd);
          } else {
            wakeArmedRef.current = true;
            clearTimeout(wakeArmTimerRef.current);
            wakeArmTimerRef.current = setTimeout(() => {
              wakeArmedRef.current = false;
            }, 4000);
            setLastIntent({ intent: "wake", at: Date.now() });
          }
        } else if (wakeArmedRef.current) {
          const cmd = _matchCommand("trouvé " + text);
          if (cmd) {
            setLastIntent({ intent: cmd, at: Date.now() });
            onCommandRef.current?.(cmd);
            wakeArmedRef.current = false;
            clearTimeout(wakeArmTimerRef.current);
          }
        }
      }
    }
  }, [ttsActive]);

  const dispatch = useCallback((intent) => {
    setLastIntent({ intent, at: Date.now() });
    onCommandRef.current?.(intent);
  }, []);

  const handleResult = useCallback(
    (event) => {
      // Process every interim + final result. The recognizer fires
      // multiple results per session; we treat ANY result as a chance
      // to match wake or command.
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        // Check ALL alternatives the recognizer returned (maxAlternatives=2),
        // not just the top one. ASR confidence on "Trouvé" is unstable;
        // the 2nd alternative often catches what the 1st missed.
        const alts = [];
        for (let a = 0; a < result.length; a++) {
          const t = result[a]?.transcript || "";
          if (t.trim()) alts.push(t);
        }
        if (alts.length === 0) continue;

        // If TTS is currently playing, BUFFER the most recent transcript
        // (taking the longest alternative — usually has more context) so
        // we can re-evaluate it the moment TTS ends. We deliberately do
        // NOT match here, to avoid mistaking Trouvé's own voice for a
        // command. The TTS-end effect handles deferred dispatch.
        if (ttsActiveRef.current) {
          const longest = alts.reduce((a, b) => (b.length > a.length ? b : a));
          bufferedTranscriptRef.current = longest;
          bufferedAtRef.current = Date.now();
          continue;
        }

        // Try matching against each alternative in turn. First match wins.
        let dispatched = false;
        for (const text of alts) {
          // 1. If wake word is in this same utterance, try to match a
          //    command in the rest of it. "hey trouvé next" → "next".
          if (_matchWake(text)) {
            const cmd = _matchCommand(text);
            if (cmd) {
              dispatch(cmd);
              wakeArmedRef.current = false;
              clearTimeout(wakeArmTimerRef.current);
              dispatched = true;
              break;
            }
            // No command yet — arm the wake state for ~4s so the next
            // utterance gets matched directly.
            wakeArmedRef.current = true;
            clearTimeout(wakeArmTimerRef.current);
            wakeArmTimerRef.current = setTimeout(() => {
              wakeArmedRef.current = false;
            }, 4000);
            setLastIntent({ intent: "wake", at: Date.now() });
            dispatched = true;
            break;
          }

          // 2. If wake state is armed (user just said "hey trouvé") and
          //    this utterance is a command word, dispatch.
          if (wakeArmedRef.current) {
            const cmd = _matchCommand("trouvé " + text); // prepend so the matcher strips correctly
            if (cmd) {
              dispatch(cmd);
              wakeArmedRef.current = false;
              clearTimeout(wakeArmTimerRef.current);
              dispatched = true;
              break;
            }
          }
        }
        if (dispatched) continue;
      }
    },
    [dispatch],
  );

  const start = useCallback(() => {
    if (!supported || recognitionRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    // Explicitly request mic permission FIRST via getUserMedia. iOS
    // Safari + Capacitor WKWebView do not always surface a permission
    // prompt for SpeechRecognition.start() on their own — the call
    // silently fails when permission is implicit. Going through
    // getUserMedia gets us the standard OS-level prompt, then we
    // immediately stop the audio tracks (we don't need the stream,
    // SpeechRecognition opens its own internal audio path). After
    // permission resolves, we kick off the recognizer.
    const startRecognizer = () => {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = "en-US";
      rec.maxAlternatives = 2;
      rec.onresult = handleResult;
      rec.onerror = (e) => {
        if (e.error === "not-allowed" || e.error === "service-not-allowed") {
          setPermissionDenied(true);
          setListening(false);
        }
        // "no-speech" / "aborted" / "audio-capture" fire often; ignore
        // and let onend handle the restart loop.
      };
      rec.onend = () => {
        // Browsers auto-stop after silence; restart so listening feels
        // continuous. Guard against tight loops if start() throws.
        if (recognitionRef.current && !restartGuardRef.current) {
          restartGuardRef.current = true;
          setTimeout(() => {
            restartGuardRef.current = false;
            try {
              recognitionRef.current?.start();
            } catch {
              /* already running */
            }
          }, 250);
        }
      };
      try {
        rec.start();
        recognitionRef.current = rec;
        setPermissionDenied(false);
        setListening(true);
      } catch {
        // Some browsers throw if start() is called too soon after stop.
      }
    };

    if (
      typeof navigator !== "undefined" &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function"
    ) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          // Permission granted — release the stream and start the
          // recognizer. We never use the raw audio; we just needed
          // the permission grant to be cached.
          try {
            stream.getTracks().forEach((t) => t.stop());
          } catch {
            /* no-op */
          }
          startRecognizer();
        })
        .catch((err) => {
          if (
            err?.name === "NotAllowedError" ||
            err?.name === "PermissionDeniedError" ||
            err?.name === "SecurityError"
          ) {
            setPermissionDenied(true);
            setListening(false);
          } else {
            // Other failures (no mic on this device, audio busy on
            // Android) — still try to start the recognizer; it might
            // succeed on its own permission path.
            startRecognizer();
          }
        });
    } else {
      // No getUserMedia (very old browsers) — fall back to the bare
      // SpeechRecognition path.
      startRecognizer();
    }
  }, [supported, handleResult]);

  const stop = useCallback(() => {
    const rec = recognitionRef.current;
    recognitionRef.current = null;
    setListening(false);
    wakeArmedRef.current = false;
    clearTimeout(wakeArmTimerRef.current);
    if (rec) {
      try { rec.onend = null; rec.stop(); } catch { /* no-op */ }
    }
  }, []);

  // Honour the enabled flag
  useEffect(() => {
    if (!supported) return;
    if (enabled) start();
    else stop();
    return stop;
  }, [enabled, supported, start, stop]);

  // Re-check support on mount (some browsers expose it lazily)
  useEffect(() => {
    setSupported(isVoiceCommandsSupported());
  }, []);

  return {
    supported,
    listening,
    lastIntent,
    permissionDenied,
    wakeArmed: wakeArmedRef.current,
    // True while TTS is speaking — UI can show "listening (paused)"
    // so the user knows their next utterance will be processed the
    // moment Trouvé finishes.
    mutedByTts: !!ttsActive,
  };
};

// Exported for tests
export const __test = { _matchWake, _matchCommand };
